import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import AppHeader from '../components/layout/AppHeader';
import MyRewardPageHeader from '../components/bazzar/MyRewardPageHeader';
import TransactionCard from '../components/transactions/TransactionCard';
import {
  downloadTransactionInvoice,
  fetchTransactions,
} from '../services/transactionService';
import { clearAuthSession } from '../storage/authStorage';
import { BAZZAR_PATH } from '../routes/paths';

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const loadMoreRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const loadInitialTransactions = useCallback(async () => {
    setLoading(true);

    try {
      const { items, pagination } = await fetchTransactions({ page: 1 });
      setTransactions(items);
      pageRef.current = 1;
      setHasMore(Boolean(pagination?.hasNextPage));
      hasMoreRef.current = Boolean(pagination?.hasNextPage);
    } catch {
      setTransactions([]);
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreTransactions = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const nextPage = pageRef.current + 1;
      const { items, pagination } = await fetchTransactions({ page: nextPage });

      setTransactions((prev) => [...prev, ...items]);
      pageRef.current = nextPage;
      setHasMore(Boolean(pagination?.hasNextPage));
      hasMoreRef.current = Boolean(pagination?.hasNextPage);
    } catch {
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadInitialTransactions();
  }, [loadInitialTransactions]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || loading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreTransactions();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [loadMoreTransactions, loading, transactions.length]);

  const handleDownloadInvoice = (transaction) => {
    if (!transaction?.canDownloadInvoice) {
      toast.info(transaction?.invoiceMessage || 'Invoice will be generated shortly.');
      return;
    }

    if (downloadTransactionInvoice(transaction)) return;
    toast.info(transaction?.invoiceMessage || 'Invoice will be generated shortly.');
  };

  const handleConfirmLogout = () => {
    clearAuthSession();
    window.location.href = '/';
  };

  return (
    <MainLayout>
      <Box sx={{ px: { md: '50px' } }}>
        <AppHeader endVariant="profile" onLogoutClick={() => setLogoutDialogOpen(true)} />
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: 6, pt: { xs: 1, md: 2 } }}>
        <MyRewardPageHeader title="Transactions" backLabel="Back to Home" backTo={BAZZAR_PATH} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#ff00f2' }} />
          </Box>
        ) : transactions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography sx={{ color: '#e6d4ff', fontSize: 16, fontWeight: 600, textAlign: 'center' }}>
              No transactions yet.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              width: '100%',
              maxWidth: 745,
              mx: 'auto',
            }}
          >
            {transactions.map((transaction) => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                onDownloadInvoice={handleDownloadInvoice}
              />
            ))}

            {hasMore ? (
              <Box
                ref={loadMoreRef}
                sx={{ display: 'flex', justifyContent: 'center', py: 2, minHeight: 48 }}
              >
                {loadingMore ? <CircularProgress size={28} sx={{ color: '#ff00f2' }} /> : null}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a0834',
            border: '1px solid #ff66f766',
            color: '#fff',
            minWidth: { xs: '90%', sm: 380 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fff' }}>Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e6d4ff' }}>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: '#e6d4ff', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
